import React, { useState, useEffect } from "react";
import LeadTable from "./LeadTable";
import AddLeadModal from "./AddLeadModal";
import ExportImportButtons from "./ExportImportButtons";
import NotificationBell from "./NotificationBell";
import SettingsPage from "./SettingsPage";
import DashboardPage from "./DashboardPage";
import { fetchLeads } from "./api";
import { initialStatuses } from "./statusConfig";
import TodoPage from "./TodoPage";
import CalendarPage from "./CalendarPage";
import { AuthProvider, useAuth } from "./AuthContext";
import LoginPage from "./LoginPage";
import CalendarNotifier from "./CalendarNotifier";
import ZapierIntegration from "./ZapierIntegration";
import TaskNotifier from "./TaskNotifier";
import LeadDetailPage from "./LeadDetailPage";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import PrivateRoute from "./PrivateRoute";

const ProtectedApp = () => {
  const { token, logout } = useAuth();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshLeads, setRefreshLeads] = useState(false);
  const [leads, setLeads] = useState([]);
  const [highlightedLeadId, setHighlightedLeadId] = useState(null);
  const [statuses, setStatuses] = useState(initialStatuses);
  const [page, setPage] = useState("crm");
  const [prioritySettings, setPrioritySettings] = useState({
    urgentDays: 2,
    expiredDays: 0,
  });

  useEffect(() => {
    if (token) loadLeads();
  }, [refreshLeads, token]);

  useEffect(() => {
    if (Array.isArray(leads) && leads.length > 0) {
      checkForExpiredLeads();
    }
  }, [leads]);

  const checkForExpiredLeads = () => {
    const today = new Date().toISOString().split("T")[0]; // formato YYYY-MM-DD
    const lastShownDate = localStorage.getItem("lastExpiredLeadsAlertDate");

    const expiredLeads = leads.filter((lead) => {
      if (!lead.data_scadenza) return false;
      const scadenza = new Date(lead.data_scadenza);
      return scadenza < new Date();
    });

    if (expiredLeads.length > 0 && lastShownDate !== today) {
      alert(`📢 Hai ${expiredLeads.length} lead SCADUTI da gestire!`);
      console.log(`📩 Email inviata: Hai ${expiredLeads.length} lead da gestire. Check CRM!`);

      // ✅ salva la data di oggi per evitare notifiche multiple nello stesso giorno
      localStorage.setItem("lastExpiredLeadsAlertDate", today);
    }
  };

  const loadLeads = async () => {
    if (!token) return;  // 👈 evita la chiamata senza login
    try {
      const data = await fetchLeads();
      setLeads(data);
    } catch (err) {
      console.error("Errore caricamento leads:", err);
    }
  };


  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  const handleLeadAdded = () => setRefreshLeads(!refreshLeads);

  const handleSelectLead = (leadId) => {
    const row = document.getElementById(`lead-${leadId}`);
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedLeadId(leadId);
      setTimeout(() => setHighlightedLeadId(null), 3000);
    }
  };

  if (!token) {
    return (
      <LoginPage
        onLoginSuccess={(email) => {
          localStorage.setItem("userEmail", email);
        }}
      />
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen p-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-2">
        <h1 className="text-3xl font-bold">CRM Leads Table</h1>
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <button onClick={() => setPage("crm")} className={`px-4 py-2 rounded ${page === "crm" ? "bg-blue-500 text-white" : "bg-gray-300"}`}>📋 CRM</button>
          <button onClick={() => setPage("dashboard")} className={`px-4 py-2 rounded ${page === "dashboard" ? "bg-blue-500 text-white" : "bg-gray-300"}`}>📊 Dashboard</button>
          <button onClick={() => setPage("todo")} className={`px-4 py-2 rounded ${page === "todo" ? "bg-blue-500 text-white" : "bg-gray-300"}`}>📝 To Do</button>
          <button onClick={() => setPage("calendar")} className={`px-4 py-2 rounded ${page === "calendar" ? "bg-blue-500 text-white" : "bg-gray-300"}`}>📅 Calendario</button>
          <button onClick={() => setPage("settings")} className={`px-4 py-2 rounded ${page === "settings" ? "bg-blue-500 text-white" : "bg-gray-300"}`}>⚙️ Impostazioni</button>
          <button onClick={() => setPage("zapier")} className={`px-4 py-2 rounded ${page === "zapier" ? "bg-blue-500 text-white" : "bg-gray-300"}`}>🤖 Zapier</button>
          <button onClick={logout} className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"> 🔓 Logout </button>
        </div>
      </div>

      {page === "crm" && (
        <>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-4">
            <button onClick={openModal} className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
              ➕ Aggiungi Lead
            </button>
            <NotificationBell leads={leads} onSelectLead={handleSelectLead} />
          </div>
          {isModalOpen && <AddLeadModal onClose={closeModal} onLeadAdded={handleLeadAdded} />}
          <ExportImportButtons onLeadsImported={() => setRefreshLeads(!refreshLeads)} />
          <LeadTable
            refresh={refreshLeads}
            highlightedLeadId={highlightedLeadId}
            statuses={statuses}
            prioritySettings={prioritySettings}
          />
        </>
      )}

      {page === "dashboard" && <DashboardPage leads={leads} statuses={statuses} />}
      {page === "todo" && <TodoPage leads={leads} />}
      {page === "calendar" && <CalendarPage />}
      {page === "settings" && (
        <SettingsPage
          statuses={statuses}
          setStatuses={setStatuses}
          prioritySettings={prioritySettings}
          setPrioritySettings={setPrioritySettings}
        />
      )}
      {page === "zapier" && <ZapierIntegration />}
    </div>
  );
};

function App() {
  const email = localStorage.getItem("userEmail");

  return (
    <AuthProvider>
      <BrowserRouter>
        {email && <CalendarNotifier userEmail={email} />}
        <TaskNotifier />
        <Routes>
          {/* 🔓 Rotta pubblica */}
          <Route path="/login" element={<LoginPage />} />

          {/* 🔒 Rotte protette */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <ProtectedApp />
              </PrivateRoute>
            }
          />
          <Route
            path="/lead/:id"
            element={
              <PrivateRoute>
                <LeadDetailPage />
              </PrivateRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
